import { useEffect, useState } from "react";
import axios from "axios";
import {
  Dialog,
  DialogContent,
  DialogHeader,
  DialogTitle,
  DialogFooter,
} from "../ui/dialog";
import { Button } from "../ui/button";
import { Input } from "../ui/input";
import { Label } from "../ui/label";
import { Pause, Play } from "lucide-react";
// 29/09/2026 — date gg/mm/aaaa da un'unica fonte.
import { formatDate } from "../../lib/format";

/**
 * SuspendPolicyDialog - Dialog for suspending a policy
 */
export function SuspendPolicyDialog({
  open,
  onOpenChange,
  policy,
  getClientName,
  suspensionData,
  setSuspensionData,
  onSuspend
}) {
  return (
    <Dialog open={open} onOpenChange={onOpenChange}>
      <DialogContent>
        <DialogHeader>
          <DialogTitle className="flex items-center gap-2">
            <Pause className="h-5 w-5 text-orange-500" />
            Sospendi Polizza
          </DialogTitle>
        </DialogHeader>
        <div className="space-y-4 py-4">
          {policy && (
            <div className="p-3 bg-muted rounded-lg">
              <p className="font-medium">{policy.policy_number}</p>
              <p className="text-sm text-muted-foreground">
                {getClientName(policy.client_id)} - {policy.company}
              </p>
            </div>
          )}
          <div className="space-y-2">
            <Label htmlFor="suspension_reminder">Data Promemoria *</Label>
            <Input
              id="suspension_reminder"
              type="date"
              value={suspensionData.suspension_reminder}
              onChange={(e) => setSuspensionData({ ...suspensionData, suspension_reminder: e.target.value })}
              data-testid="suspension-reminder-input"
            />
            <p className="text-xs text-muted-foreground">
              Riceverai un promemoria Telegram in questa data
            </p>
          </div>
          <div className="space-y-2">
            <Label htmlFor="suspension_reason">Motivo (opzionale)</Label>
            <Input
              id="suspension_reason"
              value={suspensionData.suspension_reason}
              onChange={(e) => setSuspensionData({ ...suspensionData, suspension_reason: e.target.value })}
              placeholder="Es: Veicolo in vendita, Incidente..."
              data-testid="suspension-reason-input"
            />
          </div>
        </div>
        <DialogFooter>
          <Button variant="outline" onClick={() => onOpenChange(false)}>Annulla</Button>
          <Button onClick={onSuspend} className="bg-orange-600 hover:bg-orange-700" data-testid="confirm-suspend-btn">
            <Pause className="h-4 w-4 mr-2" />
            Sospendi
          </Button>
        </DialogFooter>
      </DialogContent>
    </Dialog>
  );
}

/**
 * ReactivatePolicyDialog - Dialog for reactivating a suspended policy
 *
 * 29/10/2026: si apre gia' compilata. Il CRM ha congelato i giorni di copertura
 * residui alla sospensione e li restituisce da oggi: la nuova scadenza e' calcolata
 * (modificabile). La data di inizio cambia solo se la scrivi.
 */
export function ReactivatePolicyDialog({
  open,
  onOpenChange,
  policy,
  getClientName,
  reactivationData,
  setReactivationData,
  onReactivate
}) {
  const [anteprima, setAnteprima] = useState(null);

  useEffect(() => {
    if (!open || !policy?.policy_id) return;
    let annullato = false;
    setAnteprima(null);
    axios.get(`/api/policies/${policy.policy_id}/reactivation-preview`)
      .then(({ data }) => {
        if (annullato) return;
        setAnteprima(data);
        if (data?.nuova_scadenza) {
          setReactivationData((d) => ({ ...d, new_expiry_date: d.new_expiry_date || data.nuova_scadenza }));
        }
      })
      .catch(() => { if (!annullato) setAnteprima({ errore: true }); });
    return () => { annullato = true; };
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [open, policy?.policy_id]);

  return (
    <Dialog open={open} onOpenChange={onOpenChange}>
      <DialogContent>
        <DialogHeader>
          <DialogTitle className="flex items-center gap-2">
            <Play className="h-5 w-5 text-green-500" />
            Riattiva Polizza
          </DialogTitle>
        </DialogHeader>
        <div className="space-y-4 py-4">
          {policy && (
            <div className="p-3 bg-muted rounded-lg">
              <p className="font-medium">{policy.policy_number}</p>
              <p className="text-sm text-muted-foreground">
                {getClientName(policy.client_id)} - {policy.company}
              </p>
              {policy.original_expiry_date && (
                <p className="text-xs text-muted-foreground mt-1">
                  Scadenza originale: {formatDate(policy.original_expiry_date, "-")}
                </p>
              )}
            </div>
          )}
          {anteprima?.giorni_residui != null && (
            <div className="p-3 rounded-lg border border-green-200 bg-green-50 text-sm" data-testid="reactivate-preview">
              Sospesa dal <b>{formatDate(anteprima.sospesa_dal, "-")}</b> con <b>{anteprima.giorni_residui} giorni</b> di copertura ancora da usare.
              Riattivandola oggi la nuova scadenza è <b>{formatDate(anteprima.nuova_scadenza, "-")}</b>.
            </div>
          )}
          {anteprima?.errore && (
            <p className="text-xs text-orange-700">Non riesco a calcolare la nuova scadenza: scrivila a mano.</p>
          )}
          <div className="space-y-2">
            <Label htmlFor="new_expiry_date">Nuova Data Scadenza</Label>
            <Input
              id="new_expiry_date"
              type="date"
              value={reactivationData.new_expiry_date}
              onChange={(e) => setReactivationData({ ...reactivationData, new_expiry_date: e.target.value })}
              data-testid="reactivate-expiry-date-input"
            />
            <p className="text-xs text-muted-foreground">
              Calcolata in automatico: correggila solo se la compagnia ha fatto un conto diverso.
            </p>
          </div>
          <div className="space-y-2">
            <Label htmlFor="new_start_date">Nuova Data Inizio (facoltativa)</Label>
            <Input
              id="new_start_date"
              type="date"
              value={reactivationData.new_start_date}
              onChange={(e) => setReactivationData({ ...reactivationData, new_start_date: e.target.value })}
              data-testid="reactivate-start-date-input"
            />
            <p className="text-xs text-muted-foreground">
              Lasciala vuota per tenere la data di inizio originale.
            </p>
          </div>
        </div>
        <DialogFooter>
          <Button variant="outline" onClick={() => onOpenChange(false)}>Annulla</Button>
          <Button onClick={onReactivate} className="bg-green-600 hover:bg-green-700" data-testid="confirm-reactivate-btn">
            <Play className="h-4 w-4 mr-2" />
            Riattiva
          </Button>
        </DialogFooter>
      </DialogContent>
    </Dialog>
  );
}

export default SuspendPolicyDialog;
